import { useState, useEffect, useMemo } from "react";
import { Button, Alert } from "reactstrap";
import useAddWorkspace from "api/api-hooks/workspaces/useAddWorkspace";
import useUpdateWorkspace from "api/api-hooks/workspaces/useUpdateWorkspace";
import AppDrawer from "components/Drawers/AppDrawer";
import WorkspaceInfoSection from "./WorkspaceInfoSection";
import WorkspaceFiltersSection from "./WorkspaceFiltersSection";
import WorkspaceUsersSection from "./WorkspaceUsersSection";
import WorkspacePrefillsSection from "./WorkspacePrefillsSection";
import useUser from "api/api-hooks/auth/useUser";
import { useAtomValue } from "jotai";
import _ from "underscore";
import useTenantUsers from "api/api-hooks/tenantUsers/useTenantUsers";
import { tenantIDAtom } from "atoms/GlobalAtoms";
import AppSpinner from "../../../elements/AppSpinner";

const getInitialValues = (currentUser, ownerPermissionID) => {
  return {
    Name: "",
    Description: "",
    Privacy: "public",
    Filters: [],
    Prefills: [],
    WorkspaceUsers: [
      {
        Email: currentUser?.PrimaryContactEmail,
        TenantOwner: true, // We are following the Format used in CloudUsersGroup here.
        PermissionsID: ownerPermissionID, //We will map values on creation to more descriptive names.
      },
    ],
  };
};

// Drawer used to Create or Update a Workspace
// Provide existing workspace for Update mode, or leave empty(null) for Create mode
const EditWorkspaceDrawer = ({
  open,
  handleClose,
  existingWorkspace = null,
}) => {
  const [section, setSection] = useState(0);
  const { addWorkspace, addSubmitting, addError } = useAddWorkspace();
  const { updateWorkspace, updateSubmitting, updateError } =
    useUpdateWorkspace();
  const [values, setValues] = useState(null);
  const { user, userLoading, wPermissions: workspacePermissions } = useUser();
  const { users } = useTenantUsers();
  const tenantID = useAtomValue(tenantIDAtom);

  const onChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const onClose = () => {
    handleClose();
    setValues(getInitialValues(user, ownerPermissions?.id));
    setSection(0);
  };

  // For Creating or Updating a Workspace
  const handleUpdateWorkspace = () => {
    const finalValues = { ...values };

    finalValues.WorkspaceUsers = values.WorkspaceUsers.map((user) => {
      const foundUser = _.findWhere(users, { PrimaryContactEmail: user.Email });
      const foundTenant = _.findWhere(foundUser?.Tenants, {
        TenantID: tenantID,
      });
      const userPermissionsID = foundTenant?.PermissionsID;

      return {
        Email: user.Email,
        Name: foundUser?.Name,
        PersonID: foundUser?.id,
        Owner: user.TenantOwner,
        WorkspacePermissionsID: user.PermissionsID, // The id for the user's workspace permissions profile.
        PermissionsID: userPermissionsID, // The id for the user's permission object in the Permissions container
      };
    });

    if (existingWorkspace) {
      const payload = {};

      // Calculate the payload based on the values that changed
      for (const key of Object.keys(values)) {
        if (values[key] !== existingWorkspace[key]) {
          payload[key] = values[key];
        }
      }

      updateWorkspace({
        payload: payload,
        onSuccess: onClose,
      });
    } else {
      addWorkspace({
        payload: finalValues,
        onSuccess: onClose,
      });
    }
  };

  const isDisabled = () => {
    if (section === 0 && !values?.Name) {
      return true;
    }

    if (section === 1 && values?.Filters?.length) {
      for (const filter of values?.Filters ?? []) {
        if (Object.keys(filter.Filter).length === 0) {
          return true;
        }
      }
    }

    if (section === 2 && values?.Prefills?.length) {
      for (const prefill of values?.Prefills ?? []) {
        if (prefill.Prefills.length === 0) {
          return true;
        }
      }
    }

    return false;
  };

  const ownerPermissions = useMemo(() => {
    if (!workspacePermissions?.length) return null;

    return _.findWhere(workspacePermissions, {
      Type: "Workspace",
      Owner: true,
    });
  }, [workspacePermissions]);

  // Sync the values with the existing workspace
  useEffect(() => {
    if (userLoading || !ownerPermissions) return;

    if (existingWorkspace !== null) {
      setValues({
        Name: existingWorkspace.Name,
        Description: existingWorkspace.Description,
        Privacy: existingWorkspace.Privacy,
      });
    } else {
      setValues(getInitialValues(user, ownerPermissions.id));
    }
  }, [userLoading, user, ownerPermissions, existingWorkspace]);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      size="md"
      headerTitle={existingWorkspace ? "Edit Workspace" : "Create Workspace"}
      headerOptions={
        <>
          {section !== 0 && (
            <Button
              onClick={() =>
                setSection((prev) => {
                  const newSection = prev - 1;

                  if (newSection < 0) return 0;

                  return newSection;
                })
              }
              size="sm"
              style={{ width: 80 }}
            >
              Back
            </Button>
          )}
          {section < 3 && (
            <Button
              color="primary"
              size="sm"
              onClick={() =>
                setSection((prev) => {
                  const newSection = prev + 1;

                  if (newSection > 3) return 3;

                  return newSection;
                })
              }
              style={{ width: 80 }}
              disabled={isDisabled()}
            >
              Next
            </Button>
          )}
          {section === 3 && (
            <Button
              color="primary"
              disabled={
                !values?.Name ||
                isDisabled() ||
                addSubmitting ||
                updateSubmitting
              }
              onClick={handleUpdateWorkspace}
              size="sm"
              style={{ width: 80 }}
            >
              {addSubmitting || updateSubmitting ? (
                <AppSpinner style={{ height: 16, width: 16 }} color="white" />
              ) : existingWorkspace ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          )}
        </>
      }
      bodyCSS="pt-2"
      body={
        <>
          {section === 0 && (
            <WorkspaceInfoSection values={values} onChange={onChange} />
          )}
          {section === 1 && (
            <WorkspaceFiltersSection values={values} setValues={setValues} />
          )}
          {section === 2 && (
            <WorkspacePrefillsSection values={values} setValues={setValues} />
          )}
          {section === 3 && (
            <WorkspaceUsersSection values={values} onChange={onChange} />
          )}
          {(addError || updateError) && (
            <Alert color="danger" className="my-0 p-2">
              {addError || updateError}
            </Alert>
          )}
        </>
      }
    />
  );
};

export default EditWorkspaceDrawer;
